import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';
import { logger } from './logger';

/**
 * Core Web Vitals Thresholds (as of 2024)
 * - LCP (Largest Contentful Paint): Good ≤ 2.5s, Needs Improvement ≤ 4s, Poor > 4s
 * - INP (Interaction to Next Paint): Good ≤ 200ms, Needs Improvement ≤ 500ms, Poor > 500ms
 * - CLS (Cumulative Layout Shift): Good ≤ 0.1, Needs Improvement ≤ 0.25, Poor > 0.25
 */

type RatingColor = 'green' | 'yellow' | 'red';

function getRating(metric: Metric): RatingColor {
  if (metric.rating === 'good') return 'green';
  if (metric.rating === 'needs-improvement') return 'yellow';
  return 'red';
}

function formatValue(metric: Metric): string {
  if (metric.name === 'CLS') {
    return metric.value.toFixed(3);
  }
  return `${Math.round(metric.value)}ms`;
}

function logMetric(metric: Metric) {
  const color = getRating(metric);
  const colorCode = color === 'green' ? '#22c55e' : color === 'yellow' ? '#eab308' : '#ef4444';
  
  logger.log(
    `%c[Web Vitals] ${metric.name}: ${formatValue(metric)} (${metric.rating})`,
    `color: ${colorCode}; font-weight: bold;`
  );
  
  // In production, you could send this to your analytics service
  // Example: sendToAnalytics({ name: metric.name, value: metric.value, rating: metric.rating });
}

/**
 * Initialize Core Web Vitals monitoring
 * Reports metrics to console in development
 * Can be extended to send to analytics in production
 */
export function initWebVitals() {
  // Core Web Vitals (the big three that affect ranking)
  onLCP(logMetric);  // Largest Contentful Paint - loading performance
  onINP(logMetric);  // Interaction to Next Paint - responsiveness (replaced FID in 2024)
  onCLS(logMetric);  // Cumulative Layout Shift - visual stability
  
  // Additional metrics (useful for debugging)
  onFCP(logMetric);  // First Contentful Paint
  onTTFB(logMetric); // Time to First Byte
}

/**
 * Get Web Vitals as a promise (useful for testing or one-time checks)
 */
export async function getWebVitals(): Promise<Record<string, Metric>> {
  const metrics: Record<string, Metric> = {};
  
  return new Promise((resolve) => {
    let count = 0;
    const total = 5;
    
    const collect = (metric: Metric) => {
      metrics[metric.name] = metric;
      count++;
      if (count >= total) {
        resolve(metrics);
      }
    };
    
    onLCP(collect);
    onINP(collect);
    onCLS(collect);
    onFCP(collect);
    onTTFB(collect);
    
    // Resolve after 10 seconds even if not all metrics are collected
    setTimeout(() => resolve(metrics), 10000);
  });
}
